const PENDING_AUTH0_RETURN_TO_KEY = 'auth0:pendingReturnTo';
export const AUTH0_CALLBACK_PATH = '/auth/callback';
const DEFAULT_AUTH0_RETURN_TO = '/';

type CallbackLocation = Pick<Location, 'pathname' | 'replace'>;

let inMemoryPendingReturnTo: string | undefined;
let hasNavigatedFromCallback = false;

export function isAuth0CallbackPath(pathname: string): boolean {
    return pathname.endsWith(AUTH0_CALLBACK_PATH);
}

export function setPendingAuth0ReturnTo(returnTo: string): void {
    inMemoryPendingReturnTo = returnTo;
    hasNavigatedFromCallback = false;

    try {
        globalThis.sessionStorage.setItem(PENDING_AUTH0_RETURN_TO_KEY, returnTo);
    } catch {
        // Private mode or quota errors must not block the Auth0 callback.
        // inMemoryPendingReturnTo still holds the destination for this page load.
    }
}

/**
 * Removes OAuth `code`/`state` from the callback URL after the SDK has
 * exchanged them, so a refresh cannot replay a spent authorization code.
 * Stays on the current callback path until Auth0TokenSync document-navigates.
 */
export function clearAuth0CallbackSearch(
    location: Pick<Location, 'pathname'> = globalThis.window.location,
    history: Pick<History, 'replaceState'> = globalThis.window.history
): void {
    history.replaceState({}, '', location.pathname);
}

function consumePendingAuth0ReturnTo(): string | null {
    let fromStorage: string | null = null;

    try {
        fromStorage = globalThis.sessionStorage.getItem(PENDING_AUTH0_RETURN_TO_KEY);
        globalThis.sessionStorage.removeItem(PENDING_AUTH0_RETURN_TO_KEY);
    } catch {
        // Fall back to the in-memory destination when storage is unavailable.
    }

    const returnTo = fromStorage ?? inMemoryPendingReturnTo ?? null;
    inMemoryPendingReturnTo = undefined;
    return returnTo;
}

/**
 * Finishes Auth0 login with a document navigation so Next.js cookie redirects
 * (Treats, account) see `accessToken`. No-ops off `/auth/callback` so later
 * `Auth0TokenSync` runs cannot hijack browsing. On the callback route, falls
 * back to `/` when nothing is pending so a sessionStorage failure cannot leave
 * the user on the loading screen.
 */
export function completeAuth0CallbackRedirect(location: CallbackLocation = globalThis.window.location): void {
    if (!isAuth0CallbackPath(location.pathname) || hasNavigatedFromCallback) {
        return;
    }

    hasNavigatedFromCallback = true;
    location.replace(consumePendingAuth0ReturnTo() ?? DEFAULT_AUTH0_RETURN_TO);
}
